import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Search, Mail, Phone, Building2, Briefcase, ChevronDown, ChevronUp,
    Calendar, Download, FileText, Handshake, Users as UsersIcon,
    BadgeCheck, Network,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError, API } from "@/lib/api";

const INTEREST_META = {
    tltb_partnership_meeting: { label: "Partnership meeting", icon: Handshake, accent: "coral" },
    mataqali_introduction: { label: "Mataqali intro", icon: UsersIcon, accent: "fiji-green" },
    joint_venture_briefing: { label: "JV briefing", icon: Briefcase, accent: "ocean" },
    regulatory_alignment: { label: "Regulatory alignment", icon: BadgeCheck, accent: "ocean" },
    other: { label: "Other", icon: Network, accent: "muted" },
};

const STATUS_META = {
    new: { className: "bg-secondary text-foreground border-border", label: "NEW" },
    in_progress: { className: "bg-coral/15 text-coral border-coral/30", label: "IN PROGRESS" },
    met: { className: "bg-fiji-green/15 text-fiji-green border-fiji-green/30", label: "MET" },
    closed: { className: "bg-muted text-muted-foreground border-border", label: "CLOSED" },
};

const ACCENT_CLASSES = {
    coral: "text-coral bg-coral/15",
    "fiji-green": "text-fiji-green bg-fiji-green/15",
    ocean: "text-ocean dark:text-turquoise bg-ocean/15 dark:bg-turquoise/15",
    muted: "text-foreground bg-secondary",
};

export default function AdminTLTBContactsTab() {
    const [data, setData] = useState({ items: [], stats: null });
    const [loading, setLoading] = useState(true);
    const [interestFilter, setInterestFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (interestFilter !== "all") params.set("interest", interestFilter);
            if (statusFilter !== "all") params.set("status", statusFilter);
            const { data: d } = await api.get(`/admin/tltb-contacts?${params}`);
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not load TLTB contacts");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interestFilter, statusFilter]);

    const filtered = data.items.filter((c) => {
        if (!search.trim()) return true;
        const s = search.trim().toLowerCase();
        return (
            (c.email || "").toLowerCase().includes(s) ||
            (c.full_name || "").toLowerCase().includes(s) ||
            (c.organisation || "").toLowerCase().includes(s) ||
            (c.role || "").toLowerCase().includes(s)
        );
    });

    return (
        <div data-testid="tltb-contacts-tab" className="space-y-6">
            {data.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tltb-stats">
                    <StatTile label="Total contacts" value={data.stats.total_contacts} accent="ocean" />
                    <StatTile
                        label="PDF downloads"
                        value={data.stats.pdf_downloads}
                        accent="coral"
                        action={
                            <a
                                href={`${API}/tltb/pack.pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="tltb-stat-pdf-link"
                                className="text-[10px] uppercase tracking-wider text-coral hover:underline font-app font-bold flex items-center gap-1"
                            >
                                <Download className="h-3 w-3" /> View pack
                            </a>
                        }
                    />
                    <StatTile
                        label="In progress"
                        value={data.stats.by_status?.in_progress || 0}
                        accent="coral"
                        filterId="in_progress"
                        onClick={setStatusFilter}
                    />
                    <StatTile
                        label="Met / Closed"
                        value={(data.stats.by_status?.met || 0) + (data.stats.by_status?.closed || 0)}
                        accent="fiji-green"
                    />
                </div>
            )}

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
                <div className="flex flex-wrap gap-2 items-center">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-40 bg-card text-foreground" data-testid="tltb-status-filter">
                            <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="in_progress">In progress</SelectItem>
                            <SelectItem value="met">Met</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={interestFilter} onValueChange={setInterestFilter}>
                        <SelectTrigger className="h-9 w-56 bg-card text-foreground" data-testid="tltb-interest-filter">
                            <SelectValue placeholder="All interests" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All interests</SelectItem>
                            {Object.entries(INTEREST_META).map(([id, meta]) => (
                                <SelectItem key={id} value={id}>{meta.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        data-testid="tltb-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, org, email…"
                        className="pl-8 h-9 bg-card text-foreground w-72"
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-center text-muted-foreground font-app py-12" data-testid="tltb-loading">Loading TLTB contacts…</div>
            ) : filtered.length === 0 ? (
                <Card className="p-10 text-center bg-card" data-testid="tltb-empty">
                    <Handshake className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                    <div className="font-display text-lg font-bold text-foreground mb-1">No TLTB contacts yet</div>
                    <p className="font-app text-sm text-muted-foreground">
                        Submissions from /tltb-partnership will appear here as TLTB stakeholders engage.
                    </p>
                </Card>
            ) : (
                <div className="space-y-2">
                    {filtered.map((c) => (
                        <ContactRow
                            key={c.id}
                            contact={c}
                            expanded={expandedId === c.id}
                            onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, accent = "ocean", filterId, onClick, action }) {
    const accents = {
        ocean: "border-ocean/30 text-ocean dark:text-turquoise",
        coral: "border-coral/30 text-coral",
        "fiji-green": "border-fiji-green/30 text-fiji-green",
        muted: "border-border text-foreground",
    };
    const clickable = onClick && filterId;
    return (
        <Card
            className={`p-3 bg-card border ${accents[accent]} ${clickable ? "cursor-pointer hover:shadow-md transition" : ""}`}
            onClick={clickable ? () => onClick(filterId) : undefined}
            data-testid={`tltb-stat-${filterId || label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground mb-0.5">{label}</div>
            <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
            {action && <div className="mt-1.5">{action}</div>}
        </Card>
    );
}

function ContactRow({ contact, expanded, onToggle }) {
    const meta = INTEREST_META[contact.interest] || INTEREST_META.other;
    const Icon = meta.icon;
    const accentClass = ACCENT_CLASSES[meta.accent] || ACCENT_CLASSES.muted;
    const statusMeta = STATUS_META[contact.status] || STATUS_META.new;

    return (
        <Card data-testid={`tltb-row-${contact.id}`} className="bg-card overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                data-testid={`tltb-toggle-${contact.id}`}
                className="w-full text-left p-4 hover:bg-secondary/40 dark:hover:bg-card/60 transition flex items-center gap-4"
            >
                <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${accentClass}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Badge variant="outline" className={`font-app text-[10px] ${statusMeta.className}`}>
                            {statusMeta.label}
                        </Badge>
                        <Badge variant="outline" className="font-app text-[10px] border-coral/30 text-coral">
                            {meta.label}
                        </Badge>
                    </div>
                    <div className="font-display text-base font-bold text-foreground truncate">
                        {contact.full_name}
                    </div>
                    <div className="text-xs font-app text-muted-foreground flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <Building2 className="h-3 w-3" />
                        <span className="truncate font-semibold text-foreground/80">{contact.organisation}</span>
                        {contact.role && (
                            <>
                                <span className="text-muted-foreground/40">·</span>
                                <span>{contact.role}</span>
                            </>
                        )}
                    </div>
                </div>
                <div className="hidden md:flex flex-col items-end shrink-0">
                    <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground">Submitted</div>
                    <div className="text-xs font-app text-foreground">
                        {new Date(contact.created_at).toLocaleDateString()}
                    </div>
                </div>
                {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
            </button>

            {expanded && (
                <div className="border-t border-border bg-secondary/20 dark:bg-card/30 p-4 space-y-3" data-testid={`tltb-detail-${contact.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <DetailField label="Email" icon={Mail} value={contact.email} mono />
                        <DetailField label="Phone" icon={Phone} value={contact.phone || "—"} mono />
                        <DetailField label="Submitted" icon={Calendar} value={new Date(contact.created_at).toLocaleString()} />
                        <DetailField label="Status" value={statusMeta.label} />
                    </div>
                    <Card className="p-3 bg-muted/40 dark:bg-card/60 border-border">
                        <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground font-bold mb-1">Message</div>
                        <p className="font-app text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">{contact.message}</p>
                    </Card>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            asChild
                            size="sm"
                            data-testid={`tltb-row-email-${contact.id}`}
                            className="bg-ocean-green text-white font-app"
                        >
                            <a href={`mailto:${contact.email}?subject=Re%3A%20Bula%20Real%20Estate%20Fiji%20%C2%B7%20TLTB%20Partnership%20Pack`}>
                                <Mail className="h-3.5 w-3.5 mr-1.5" /> Reply via email
                            </a>
                        </Button>
                        <Button
                            asChild
                            size="sm"
                            variant="outline"
                            data-testid={`tltb-row-pdf-${contact.id}`}
                            className="font-app"
                        >
                            <a href={`${API}/tltb/pack.pdf`} target="_blank" rel="noopener noreferrer">
                                <FileText className="h-3.5 w-3.5 mr-1.5" /> Open partnership pack
                            </a>
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function DetailField({ label, value, icon: Icon, mono = false }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-app text-muted-foreground font-bold mb-1 flex items-center gap-1">
                {Icon && <Icon className="h-3 w-3" />}
                {label}
            </div>
            <div className={`font-app text-sm text-foreground/90 ${mono ? "font-mono" : ""} break-all`}>
                {value}
            </div>
        </div>
    );
}
